import styled, { css } from 'styled-components';
import { DEVICES, COLORS, pickThemeFontStyles } from '@/theme';
import { BidStatusEnum } from '@/models/bidding.model';

export const StyledBidCard = styled.div<{
  $status?: BidStatusEnum;
  $isModal?: boolean;
}>`
  border: 1px solid ${COLORS.LIGHT_PURPLE};
  border-radius: 0.5rem;
  background-color: ${({ $isModal }) => ($isModal ? COLORS.BG_SECONDARY : COLORS.WHITE)};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  ${({ $status, $isModal }) => {
    if ($isModal) {
      return css``;
    }
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return css`
          background-color: ${COLORS.WHITE_SMOKE};
        `;
      case BidStatusEnum.REJECTED:
        return css`
          background-color: ${COLORS.LIGHTER_RED};
        `;
      case BidStatusEnum.ACCEPTED:
        return css`
          background-color: ${COLORS.DARK_GREEN_LIGHT};
        `;
      case BidStatusEnum.WAITING_CONFIRMATION:
        return css`
          background-color: ${COLORS.PEACH_LIGHT};
        `;
      case BidStatusEnum.CONFIRMED:
      default:
        return css``;
    }
  }}
`;

export const StyledBidInfo = styled.div<{ $isModal?: boolean }>`
  display: flex;
  width: 100%;
  gap: 1rem;
  flex-direction: column;

  ${({ $isModal }) =>
    !$isModal &&
    css`
      @media screen and ${DEVICES.LAPTOP_S} {
        flex-direction: row;
      }
    `}
`;

export const StyledPrice = styled.div`
  ${pickThemeFontStyles('18', '26', 'BOLD')};
  color: ${COLORS.DARK_PURPLE};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and ${DEVICES.LAPTOP_S} {
    ${pickThemeFontStyles('28', '32', 'SEMI_BOLD')};
  }
`;

export const StyledBidInfoLabel = styled.span<{
  $status?: BidStatusEnum;
  $isModal?: boolean;
}>`
  ${pickThemeFontStyles('14', '20', 'SEMI_BOLD')};
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.GREY_MEDIUM;
    }
  }};

  ${({ $isModal }) =>
    !$isModal &&
    css`
      @media screen and ${DEVICES.TABLET_S} {
        ${pickThemeFontStyles('14', '20', 'NORMAL')};
      }
    `}
`;

export const StyledBidInfoValue = styled.span<{ $status?: BidStatusEnum }>`
  ${pickThemeFontStyles('16', '24', 'NORMAL')};
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.BLACK_PRIMARY;
    }
  }};
`;

export const StyledDivider = styled.div`
  height: 1px;
  background-color: ${COLORS.LIGHT_PURPLE};
  width: 100%;
`;

export const StyledBiddersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const StyledBiddersTitle = styled.h3<{ $status?: BidStatusEnum }>`
  ${pickThemeFontStyles('18', '24', 'SEMI_BOLD')};
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.BLACK_PRIMARY;
    }
  }};
  margin: 0;
  text-align: left;
`;

export const StyledValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

export const StyledRowWrapper = styled.div<{ $isModal?: boolean }>`
  display: flex;
  width: 100%;
  gap: 1rem;
  flex-direction: column;
  flex-wrap: wrap;

  ${({ $isModal }) =>
    !$isModal &&
    css`
      @media screen and ${DEVICES.TABLET_S} {
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: flex-start;

        > * {
          flex: 1 1 auto;
          min-width: 0;
        }
      }
    `}
`;

export const StyledMobilePairWrapper = styled.div<{ $isModal?: boolean }>`
  display: flex;
  width: 100%;
  gap: 1rem;
  flex-direction: row;
  flex-shrink: 0;

  > * {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 0;
  }

  ${({ $isModal }) =>
    !$isModal &&
    css`
      @media screen and ${DEVICES.TABLET_S} {
        flex: 1 1 50%;
        width: 50%;
        min-width: 0;
      }
    `}
`;

export const StyledColumnWrapper = styled.div`
  display: flex;
  width: 100%;
  gap: 1rem;
  flex-direction: column;
`;

export const StyledBidCardLabel = styled.span<{ $status?: BidStatusEnum }>`
  ${pickThemeFontStyles('14', '20')};

  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.GREY;
    }
  }};
`;

export const StyledBidCardValue = styled.span<{ $status?: BidStatusEnum }>`
  ${pickThemeFontStyles('16', '26')};
  word-break: break-word;
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.BLACK_PRIMARY;
    }
  }};
`;

export const StyledFinancingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const StyledFinancingTitle = styled.h3<{ $status?: BidStatusEnum }>`
  ${pickThemeFontStyles('18', '24', 'SEMI_BOLD')};
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.REJECTED:
        return COLORS.GREY_DARK;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      default:
        return COLORS.BLACK_PRIMARY;
    }
  }};
  margin: 0;
  text-align: left;
`;

export const StyledMobileRejectButton = styled.div`
  display: flex;
`;

export const StyledExpiredText = styled.p`
  margin: 0;
  color: ${COLORS.RED_CORAL};
`;

export const StyledStatusText = styled.p<{ $status?: BidStatusEnum }>`
  margin: 0;
  ${pickThemeFontStyles('16', '24', 'SEMI_BOLD')};
  color: ${({ $status }) => {
    switch ($status) {
      case BidStatusEnum.EXPIRED:
        return COLORS.RED_CORAL;
      case BidStatusEnum.REJECTED:
        return COLORS.RED_CORAL;
      case BidStatusEnum.ACCEPTED:
        return COLORS.DARK_GREEN;
      case BidStatusEnum.WAITING_CONFIRMATION:
        return COLORS.PEACH;
      case BidStatusEnum.CONFIRMED:
      default:
        return COLORS.BLACK_PRIMARY;
    }
  }};
`;
